import { useState, useEffect, useRef, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminApi } from '../hooks/useAdminApi';
import { MessageSquare, Send, AlertCircle, Trash2, CheckCheck, User, Radio, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const AdminMessagesPage = () => {
  const { admin } = useAdminAuth();
  const { get, post, patch, del } = useAdminApi();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('broadcast');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    const result = await get('/messages');
    if (result.success) {
      const list = [...result.data].reverse();
      setMessages(list);
      const toMark = result.data.filter(m => !m.read && m.sender_id !== admin?.id);
      for (const m of toMark) {
        await patch(`/messages/${m.id}/read`);
      }
    }
    setLoading(false);
  }, [get, patch, admin]);

  const fetchDrivers = useCallback(async () => {
    const result = await get('/driver/admin/connected-drivers');
    if (result.success && result.data.active) {
      setDrivers(result.data.active.map(item => item.driver));
    }
  }, [get]);

  useEffect(() => {
    fetchMessages();
    fetchDrivers();
    const iv = setInterval(fetchMessages, 15000);
    return () => clearInterval(iv);
  }, [fetchMessages, fetchDrivers]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    const recipientId = selectedRecipient === 'broadcast' ? null : selectedRecipient;
    const result = await post('/messages', {
      content: newMessage.trim(),
      type: 'text',
      recipient_id: recipientId
    });
    if (result.success) {
      setNewMessage('');
      fetchMessages();
      const label = recipientId
        ? `Message envoye a ${drivers.find(d => d.id === recipientId)?.first_name || 'chauffeur'}`
        : 'Broadcast envoye a tous les chauffeurs';
      toast.success(label);
    } else {
      toast.error(result.error || "Erreur d'envoi");
    }
    setSending(false);
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    const result = await del(`/messages/${msgId}`);
    if (result.success) {
      toast.success('Message supprime');
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } else {
      toast.error(result.error || 'Erreur');
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.toLocaleDateString('fr-CA', { day: '2-digit', month: 'short' })} ${d.getHours().toString().padStart(2,'0')}h${d.getMinutes().toString().padStart(2,'0')}`;
  };

  const getRecipientLabel = (msg) => {
    if (msg.recipient_id === null) return 'broadcast';
    const d = drivers.find(dr => dr.id === msg.recipient_id);
    return d ? `a ${d.first_name} ${d.last_name}` : 'a un chauffeur';
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-messages-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-amber-500/10 rounded-xl">
          <MessageSquare className="text-amber-400" size={28} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Messages</h1>
          <p className="text-slate-400 text-sm">Communication avec vos chauffeurs</p>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl min-h-[500px] max-h-[70vh] overflow-y-auto p-4 mb-4" data-testid="admin-messages-list">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-slate-500">Chargement...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
            <p>Aucun message pour l&apos;instant.</p>
            <p className="text-xs mt-1">Utilisez le champ ci-dessous pour envoyer un message a un chauffeur ou en broadcast.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map(msg => {
              const isFromAdmin = msg.sender_role && msg.sender_role !== 'driver';
              const isBroadcast = msg.recipient_id === null;
              return (
                <div key={msg.id} className={`flex ${isFromAdmin ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl p-3 ${isFromAdmin ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-slate-800 border border-slate-700'}`} data-testid={`admin-msg-${msg.id}`}>
                    <div className="flex items-center gap-2 mb-1 text-xs flex-wrap">
                      {isFromAdmin ? (
                        <span className="text-amber-400 font-semibold">Dispatch {getRecipientLabel(msg)}</span>
                      ) : (
                        <span className="text-blue-400 font-semibold flex items-center gap-1">
                          <User size={11} />
                          {msg.sender_name || 'Chauffeur inconnu'}
                        </span>
                      )}
                      {isBroadcast && (
                        <span className="text-slate-500 flex items-center gap-1">
                          <Radio size={10} /> broadcast
                        </span>
                      )}
                      {msg.type === 'alert' && (
                        <span className="text-red-400 flex items-center gap-1">
                          <AlertCircle size={11} /> alerte
                        </span>
                      )}
                    </div>
                    <p className="text-white text-sm break-words">{msg.content}</p>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-xs text-slate-500">{formatTime(msg.created_at)}</span>
                      <div className="flex items-center gap-2">
                        {msg.read && <CheckCheck className="text-blue-400" size={12} />}
                        {msg.read && (
                          <button
                            type="button"
                            onClick={() => handleDelete(msg.id)}
                            className="text-slate-500 hover:text-red-400 transition"
                            data-testid={`admin-delete-msg-${msg.id}`}
                            title="Supprimer"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form onSubmit={handleSend} className="space-y-3" data-testid="admin-messages-form">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-slate-400" />
          <label className="text-slate-400 text-sm">Destinataire:</label>
          <select
            value={selectedRecipient}
            onChange={e => setSelectedRecipient(e.target.value)}
            className="flex-1 h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm"
            data-testid="admin-messages-recipient"
          >
            <option value="broadcast">📢 Broadcast (tous les chauffeurs)</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>
                {d.first_name} {d.last_name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-3">
          <Input
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder={selectedRecipient === 'broadcast' ? 'Broadcast a tous les chauffeurs...' : 'Message prive au chauffeur selectionne...'}
            className="flex-1 h-12 bg-slate-900 border-slate-700 text-white"
            disabled={sending}
            data-testid="admin-messages-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="h-12 px-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="admin-messages-send"
          >
            <Send size={18} className="mr-2" />
            Envoyer
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminMessagesPage;
